import { fg } from '@atlaskit/platform-feature-flags';

import { type EditorAPI } from '../../types';

type ContentData = {
	data: string;
	embeddedMacroLocalId?: string;
};

interface getCustomBridgeMethodsParams {
	editorAPI?: EditorAPI;
	isEditing: boolean;
	localId: string;
}

export const getCustomBridgeMethods = ({
	isEditing,
	localId,
	editorAPI,
}: getCustomBridgeMethodsParams): {
        getEditorContent: () => Promise<ContentData>;
        getMacroContent: (bridgeData?: {
            embeddedMacroLocalId: string;
        }) => Promise<ContentData>;
        setMacroViewportHeight: (height: string) => Promise<boolean>;
        updateMacro: (newExtensionContent: ContentData) => Promise<boolean>;
    } | undefined => {
	if (!isEditing) {
		return undefined;
	}

	const getEditorContent = async (): Promise<ContentData> => {
		return new Promise((resolve) => {
			editorAPI?.core?.actions.requestDocument((doc) => {
				if (!doc) {
					resolve({ data: '' });
				} else {
					resolve({ data: JSON.stringify(doc) });
				}
			});
		});
	};

	const getMacroContent = async (bridgeData?: {
		embeddedMacroLocalId: string;
	}): Promise<ContentData> => {
		const extensionAPI = editorAPI?.extension?.actions?.api();
		if (!extensionAPI) {
			throw new Error('Unable to get macro content: extensionAPI is not available');
		}

		try {
			const macroLocalId = bridgeData?.embeddedMacroLocalId ?? localId;
			const nodeWithPos = extensionAPI.getNodeWithPosByLocalId(macroLocalId);
			const { node } = nodeWithPos;

			return { data: JSON.stringify(node) };
		} catch (error) {
			throw new Error(`Failed to get macro: ${error}`);
		}
	};

	const updateMacro = async (newExtensionContent: ContentData): Promise<boolean> => {
		if (!newExtensionContent?.data) {
			return false;
		}

		try {
			const macroLocalId = newExtensionContent?.embeddedMacroLocalId ?? localId;

			const newExtensionData = JSON.parse(newExtensionContent.data);
			const extensionAPI = editorAPI?.extension?.actions?.api();

			if (extensionAPI && newExtensionData) {
				extensionAPI.doc?.update(macroLocalId, () => newExtensionData);
				return true;
			}
		} catch (error) {
			throw new Error(`Failed to update macro: ${error}`);
		}
		return false;
	};

	const setMacroViewportHeight = async (height: string): Promise<boolean> => {
		if (!fg('platform_measure_macro_height')) {
			throw new Error('setMacroViewportHeight is not available');
		}

		try {
			const extensionAPI = editorAPI?.extension?.actions?.api();

			if (extensionAPI) {
				extensionAPI.doc?.update(localId, (currNode) => {
					const newNode = {
						attrs: {
							...currNode.attrs,
							parameters: {
								...currNode.attrs?.parameters,
								viewportHeight: height,
							},
						},
					};

					return newNode;
				});

				return true;
			}
		} catch (error) {
			throw new Error(`Failed to set the macro's viewport height: ${error}`);
		}

		return false;
	};

	return {
		getEditorContent,
		getMacroContent,
		updateMacro,
		setMacroViewportHeight,
	};
};
